import React from 'react';

/**
 * Catches render/runtime errors in its subtree so one broken component
 * (e.g. a malformed AI-generated visual) can't blank the entire app.
 *
 * Props:
 *  - fallback: optional (error, reset) => ReactNode for custom UI
 *  - label:    short name for logging/UI ("AI agent", "app")
 *  - onReset:  optional callback when the user taps "Try again"
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? ': ' + this.props.label : ''}]`, error, info?.componentStack);
  }

  handleReset() {
    this.setState({ error: null });
    this.props.onReset?.();
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback(error, this.handleReset);
    }

    return (
      <div
        role="alert"
        style={{
          padding: '24px',
          margin: '16px',
          borderRadius: '12px',
          background: 'rgba(20, 24, 40, 0.92)',
          border: '1px solid rgba(120, 140, 200, 0.35)',
          color: '#e8ecf5',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '480px'
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Something went wrong{this.props.label ? ` in the ${this.props.label}` : ''}.
        </div>
        <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 16 }}>
          The rest of the app is still running. You can retry without reloading.
        </div>
        <button
          onClick={this.handleReset}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: '#4A90E2',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
